<template>
  <div class="ForgotPassword min-h-screen flex flex-col lg:flex-row-reverse">
    <!--//v ForgotPassword form container  -->
    <transition name="auth-form" appear>
      <div
        class="ForgotPassword-form p-4 flex-1 flex flex-col justify-center md:p-8 lg:px-20 lg:max-h-screen lg:overflow-y-auto"
      >
        <div>
          <h1 class="font-minion text-xl text-primary-400 lg:text-3xl">
            Forgot Password
            <span class="block font-minion font-bold mt-0.5 lg:mt-2"
              >Ask a Fundraiser</span
            >
          </h1>

          <!-- //v form -->
          <form
            class="mt-5 flex-1 flex flex-col"
            @submit.prevent="onSubmitForm"
          >
            <div class="flex-1">
              <div class="">
                <LoginFormInput
                  input-label="Email"
                  label-for="fp-email-input"
                  input-type="text"
                  input-id="fp-email-input"
                  input-placeholder="e.g. name@domain.com"
                  v-model.trim="formData.emailVal"
                  :inputClasses="`${
                    v$.emailVal.$error
                      ? 'shakeX border-red-400 !outline-red-400 duration-300 !text-red-400 placeholder:!text-red-300'
                      : ''
                  }`"
                />
                <transition name="auth-form">
                  <p
                    class="pl-2 mt-1 text-sm text-red-400"
                    v-if="v$.emailVal.$error"
                  >
                    {{ v$.emailVal.$errors[0].$message }}
                  </p>
                </transition>
                <transition name="auth-form">
                  <p
                    class="pl-2 mt-1 text-sm text-red-400"
                    v-if="messageContent == 'Your email does not exist.'"
                  >
                    {{ messageContent }}
                  </p>
                </transition>
                <transition name="auth-form">
                  <p
                    class="pl-2 mt-1 text-sm text-red-400"
                    v-if="networkError"
                  >
                    Unable to connect to server. Please check your internet connection and try again.
                  </p>
                </transition>
                <transition name="auth-form">
                  <p
                    class="pl-2 mt-1 text-sm text-green-400"
                    v-if="
                      messageContent ==
                      'Check your email for reset password instructions.'
                    "
                  >
                    {{ messageContent }}
                  </p>
                </transition>
              </div>
              <button
                class="w-full bg-[#00423D] font-minion text-[20px] text-white h-12 md:h-14 rounded-[8px] mt-4 font-bold lg:py-4 flex items-center justify-center"
                type="submit"
                ref="submitBtn"
              >
                <transition name="button-switch" mode="out-in">
                  <span v-if="!isSendingRequest">Reset</span>
                  <div
                    v-else
                    class="max-h-8 overflow-hidden inline-flex items-center justify-center pt-1"
                  >
                    <Vue3Lottie
                      :animationData="buttonAnimation"
                      :height="120"
                      :width="120"
                    />
                  </div>
                </transition>
              </button>

              <p class="text-placeholder text-[18px] md:text-base mt-2 font-minion">
                Enter the email address you used when you joined us and we'll
                send you instructions to reset your password.
              </p>
            </div>
          </form>

          <p
            class="text-center mt-7 text-placeholder text-lg font-semibold font-minion"
          >
            Do you have an account?
            <router-link class="font-medium font-minion text-primary-400" to="/login"
              >Login</router-link
            >
          </p>
          <p
            class="text-center mt-3 text-placeholder text-lg font-semibold font-minion"
          >
            Don't have an account?
            <router-link class=" font-minion text-primary-400 font-medium" to="/sign-up"
              >Create an Account</router-link
            >
          </p>
        </div>
      </div>
    </transition>
    <AuthSlider />
  </div>
</template>
<script setup>
// v global
import global from "@/global";

//v Vue Imports
import { ref, onMounted, reactive, watch } from "vue";

//v Vue Router
import { RouterLink, RouterView, useRouter } from "vue-router";
const router = useRouter();

//v Components
import AuthSlider from "@/components/AuthSlider.vue";
import LoginFormInput from "@/components/LoginFormInput.vue";

//v Assets
import ButtonAni from "@/assets/loading-button-sending.json";

//v Vue3Lottie Animation Library
import { Vue3Lottie } from "vue3-lottie";

//v Vuelidate validation Library
import { useVuelidate } from "@vuelidate/core";
import { helpers, required, email } from "@vuelidate/validators";

//v Pinia Store: Auth
import { useAuthStore } from "@/stores/auth";
const authStore = useAuthStore();

//v View State
const buttonAnimation = ref(ButtonAni);
const isSendingRequest = ref(false);
const networkError = ref(false);
const formData = reactive({
  emailVal: "",
});
//v DOM Refs
const submitBtn = ref(null);
const messageContent = ref("");

//v From Validation Rules
const rules = {
  emailVal: {
    email: helpers.withMessage("Invalid Email Address!", email),
    required: helpers.withMessage("Field Is Required!", required),
  },
};
//v useVuelidate state
const v$ = useVuelidate(rules, formData);

watch(() => formData.emailVal, () => {
  messageContent.value = ""; // Clear the error message when the user types
});

//v Sign up Submit Function
async function onSubmitForm(e) {
  //v validate inputs
  const validationResult = await v$.value.$validate();
  if (validationResult) {
    //v Form is Valid
    isSendingRequest.value = true;
    submitBtn.value.disabled = true;
    let data = {
      email: formData.emailVal,
    };
    // console.log(data);
    try {
      networkError.value = false; // Reset network error state
      const res = await fetch(`${global.API_URL}/reset-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });
      let respone = await res.json();
      messageContent.value = respone.data;
      //v mimic sending request
      setTimeout(() => {
        isSendingRequest.value = false;
        submitBtn.value.disabled = false;
      }, 1500);
    } catch (err) {
      networkError.value = true;
      isSendingRequest.value = false;
      submitBtn.value.disabled = false;
      setTimeout(() => {
        networkError.value = false;
      }, 5000);
    }
  } else {
    //v Form is invalid
  }
}
</script>

<style scoped>
/* transition for auth forms; login, sign up, change password, forgot password, org-info */
.auth-form-enter-active,
.auth-form-leave-active {
  transition: opacity 0.3s ease-in-out, transform 0.3s ease-in-out;
}

.auth-form-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.auth-form-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.button-switch-enter-active,
.button-switch-leave-active {
  transition: opacity 0.3s ease-in-out, transform 0.4s ease-in-out;
}

.button-switch-enter-from {
  opacity: 0;
  transform: translateX(-100px);
}

.button-switch-leave-to {
  opacity: 0;
  transform: translateX(100px);
}
</style>
